import type { components } from '@tallerya/api-client';
import type { useTraduccion } from '@tallerya/i18n';
import { Chip } from '@tallerya/ui';
import { createElement, Fragment, type ReactNode } from 'react';
import type { TextoDeEntrada } from './LineaDeTiempo';

// Cómo se lee una entrada del historial de una maestra (#181): un renglón por
// guardado, y debajo lo que cambió, como decidió Mario el 21/10/2026.
//
//   ● Datos editados
//     Hoy 10:20 · Ana Batres
//     Precio de lista: 25,00 US$ → 30,00 US$
//     Cambió: nombre, notas
//
// Cada vista declara sus `Campos`: la etiqueta de cada uno y cómo se lee su valor.
// Lo que cambió sin valor —un dato personal, o un monto sin permiso de verlo—
// llega `hidden` y se junta en un solo renglón. Lo que ya se eliminó —el dueño
// anterior de un activo (#187)— llega con su nombre y sale con la etiqueta
// «Eliminado»: «Cliente: Ana López [Eliminado] → Transportes Pérez».
//
// En un alta no había nada antes, así que lo que nació con el registro se lee con su
// valor y nada más (#191): «Marca: Toyota», no «Marca: Sin marca → Toyota».

export type EntradaDeHistorial = components['schemas']['HistoryEntry'];
export type CambioDeHistorial = components['schemas']['HistoryChange'];

type Traducir = ReturnType<typeof useTraduccion>['t'];

export interface Campo {
  /** Como en el formulario: «Precio de lista». No hace falta con `frase`. */
  etiqueta?: string;
  /** Cómo se lee un valor que llegó; sin esto, tal cual. */
  valor?: (texto: string) => string;
  /** Lo que se dice cuando no hay valor; sin esto, «—». */
  vacio?: string;
  /** Una frase entera en vez de «etiqueta: de → a», para lo que no es un campo: los contactos y los activos del cliente. */
  frase?: (cambio: CambioDeHistorial) => ReactNode;
}

/** Por la clave del contrato: `list_price`, `contact_added`. */
export type Campos = Record<string, Campo>;

export function textoDeEntrada(entrada: EntradaDeHistorial, campos: Campos, t: Traducir): TextoDeEntrada {
  switch (entrada.kind) {
    // El alta trae debajo lo que vino con ella en el mismo guardado: el teléfono del
    // cliente nuevo, los otros nombres del servicio.
    case 'created':
      return { titulo: t('admin.historial.creado'), detalles: detallesDe(entrada.changes, campos, t, true) };
    case 'activated':
      return { titulo: t('admin.historial.activado') };
    case 'deactivated':
      return { titulo: t('admin.historial.desactivado') };
    case 'updated':
      return { titulo: t('admin.historial.editado'), detalles: detallesDe(entrada.changes, campos, t) };
    // El pago del gasto (#189), con el día debajo: la marca no siempre se pone el día que se pagó.
    case 'paid':
      return { titulo: t('admin.historial.pagado'), detalles: detallesDe(entrada.changes, campos, t) };
    case 'unpaid':
      return { titulo: t('admin.historial.pagoQuitado'), detalles: detallesDe(entrada.changes, campos, t) };
  }
}

/**
 * Un renglón por cambio con valor, y los que no lo tienen juntos al final. Lo usa
 * también la orden (#196), que tiene su propia forma de entrada pero los mismos
 * cambios. `creado` lee lo que nació con el registro sin su «antes».
 */
export function detallesDe(cambios: readonly CambioDeHistorial[], campos: Campos, t: Traducir, creado = false): ReactNode[] {
  const conValor = cambios.filter((c) => !c.hidden).map((c) => renglon(c, campos, t, creado));
  // Un campo que la pantalla todavía no conoce sale con su clave, antes que callarlo.
  const sinValor = cambios.filter((c) => c.hidden).map((c) => minuscula(campos[c.field]?.etiqueta ?? c.field));
  const ocultos = sinValor.length > 0 ? t('admin.historial.cambioSinValor', { campos: sinValor.join(', ') }) : undefined;

  return [...conValor, ocultos];
}

function renglon(cambio: CambioDeHistorial, campos: Campos, t: Traducir, creado: boolean): ReactNode {
  const campo = campos[cambio.field];
  if (campo?.frase) return campo.frase(cambio);

  const leer = (texto: string | null) =>
    texto === null || texto === ''
      ? (campo?.vacio ?? t('admin.historial.sinValor'))
      : campo?.valor
        ? campo.valor(texto)
        : texto;

  const etiqueta = campo?.etiqueta ?? cambio.field;
  const a = { texto: leer(cambio.to), eliminado: cambio.toDeleted };
  // Lo que nació con el registro no tenía un antes: sin el «Sin marca →».
  if (creado && cambio.from === null) {
    return fraseConEliminados(t, 'admin.historial.valor', { campo: etiqueta }, { a });
  }

  return fraseConEliminados(
    t,
    'admin.historial.cambio',
    { campo: etiqueta },
    { de: { texto: leer(cambio.from), eliminado: cambio.fromDeleted }, a },
  );
}

/**
 * `t(clave)` con la etiqueta «Eliminado» al lado de cada valor que ya no está.
 *
 * Con alguno eliminado, la frase se traduce con marcas en lugar de los valores, y
 * ahí se pone cada uno: así el orden de las palabras sigue siendo el del idioma, y
 * el valor eliminado lleva su etiqueta al lado.
 */
export function fraseConEliminados(
  t: Traducir,
  clave: Parameters<Traducir>[0],
  fijos: Record<string, string>,
  valores: Record<string, { texto: string; eliminado: boolean }>,
): ReactNode {
  const entradas = Object.entries(valores);
  if (!entradas.some(([, valor]) => valor.eliminado)) {
    return t(clave, { ...fijos, ...Object.fromEntries(entradas.map(([nombre, valor]) => [nombre, valor.texto])) });
  }

  const marcas = Object.fromEntries(entradas.map(([nombre]) => [nombre, `\uE000${nombre}\uE000`]));
  const partes = t(clave, { ...fijos, ...marcas })
    .split(/(\uE000\w+\uE000)/)
    .map((parte) => {
      const valor = parte.startsWith('\uE000') ? valores[parte.slice(1, -1)] : undefined;
      if (!valor) return parte;
      return valor.eliminado
        ? createElement(Fragment, null, valor.texto, ' ', createElement(Chip, { children: t('admin.historial.eliminado') }))
        : valor.texto;
    });

  return createElement(Fragment, null, ...partes);
}

/**
 * «Foto agregada» o «Archivo eliminado» (#195): un PDF no es una foto. Lo usan el
 * activo y la orden; `clase` es `photo` o `file`, como llega del contrato.
 */
export function textoDeAdjunto(t: Traducir, clase: string | null, agregado: boolean): string {
  const archivo = clase === 'file';
  if (agregado) return archivo ? t('admin.historial.adjunto.archivoAgregado') : t('admin.historial.adjunto.fotoAgregada');
  return archivo ? t('admin.historial.adjunto.archivoEliminado') : t('admin.historial.adjunto.fotoEliminada');
}

/** «Nombre comercial» dentro de una frase es «nombre comercial». */
export function minuscula(texto: string): string {
  return texto.charAt(0).toLocaleLowerCase() + texto.slice(1);
}
